import { Injectable, inject } from '@angular/core';
import * as L from 'leaflet';
import 'leaflet.markercluster';
import { ToastrService } from 'ngx-toastr';
import { SelectionService } from '../../services/selection.service';
import { OfflineModeService } from '../../services/offline-mode.service';
import { MapMarker, MAP_MARKER_HANDLER, MapMarkerHandler } from '../handlers';
import { LayerDetails } from '../types';

/**
 * Bridge interface for PointRenderingService to access MapComponent functionality.
 */
export interface PointRenderingBridge {
  getPoints(): MapMarker[] | undefined;
  getPointsFilter(): boolean[] | undefined;
  canReadPoints(): boolean;
  isOfflineMode(): boolean;
  wasMapDragged(): boolean;
  addLayerToMap(name: string, layer: L.Layer, active: boolean): void;
  openContextMenu(point: MapMarker): void;
  setPointsLayerVisibility(visible: boolean): void;
  getProjectValue(key: string): Promise<unknown>;
  onPointDragged?(pointId: string, newLatLng: L.LatLng, revert: () => void): void;
}

/**
 * Service für Punkt-Rendering: Marker-Erstellung, Canvas-Labels, Skalierung, Grid-Punkte, Cluster-Icons.
 * Extrahiert aus MapComponent (Plan 15 Phase 3b).
 */
@Injectable({ providedIn: 'root' })
export class PointRenderingService {
  private map!: L.Map;
  private bridge!: PointRenderingBridge;

  // Punkt-Marker
  pointsLayer?: L.MarkerClusterGroup;
  leafletMarkers = new Map<string, L.Marker>();
  draggablePoints = false;

  // Punkt Canvas-Labels
  pointLabelLayer?: L.Layer;
  readonly POINT_LABEL_MIN_ZOOM = 14;
  pointLabelRects: {x: number, y: number, w: number, h: number}[] = [];

  // Punkt-Highlighting
  assignedPointIds = new Set<string>();
  projectPointIds = new Set<string>();

  // Skalierung
  private readonly minSize = 5;
  private readonly maxSize = 14;
  private readonly minFontSize = 5;
  private readonly maxFontSize = 16;
  private lastScalingZoom = -1;

  // Grid
  gridLayer?: { ld: LayerDetails, l: L.LayerGroup };

  private msg = inject(ToastrService);
  private selectionService = inject(SelectionService);
  private oms = inject(OfflineModeService);

  init(map: L.Map, bridge: PointRenderingBridge): void {
    this.map = map;
    this.bridge = bridge;
  }

  destroy(): void {
    this._batchAbort?.abort();
    if (this.pointLabelLayer && this.map?.hasLayer(this.pointLabelLayer)) {
      this.map.removeLayer(this.pointLabelLayer);
    }
    this.pointLabelLayer = undefined;
    this.pointLabelRects.length = 0;
    this.leafletMarkers.clear();
    this.pointsLayer = undefined;
    this.assignedPointIds.clear();
    this.projectPointIds.clear();
    this.lastScalingZoom = -1;
    this.gridLayer = undefined;
  }

  // --- Marker-Erstellung ---

  createMarkersWithLabel(): void {
    // Pane für Punkte-Layer erstellen (falls noch nicht vorhanden)
    if (this.map && !this.map.getPane('layer-Punkte')) {
      const pane = this.map.createPane('layer-Punkte');
      pane.style.zIndex = '600';
    }

    const points = this.bridge.getPoints();
    const pointsFilter = this.bridge.getPointsFilter();

    // Berechne aktuelle Größe basierend auf Zoom
    const zoom = Math.round(this.map?.getZoom() ?? 13);
    const scale = Math.pow(2, zoom - 13);
    const size = Math.max(this.minSize, Math.min(this.maxSize, scale * 6));
    this.lastScalingZoom = zoom;

    const canInteract = this.bridge.canReadPoints();

    points?.forEach((point: MapMarker, index: number) => {
      if (!point.latLng) return;
      const m = L.marker(point.latLng, {
        icon: this.createSvgIcon(point.id, point.type!, size, pointsFilter && pointsFilter![index], this.selectionService.isSelected(point.id)),
        interactive: canInteract,
        bpId: point.id,
        pane: 'layer-Punkte'
      } as any);
      this.leafletMarkers.set(point.id, m);
      if (canInteract) {
        let pointerDownTime = 0;
        let pointerDownPos: L.Point | null = null;
        m.on('mousedown', (e: L.LeafletMouseEvent) => {
          pointerDownTime = Date.now();
          pointerDownPos = e.containerPoint;
        });
        m.once('add', () => {
          const el = m.getElement();
          if (el) {
            el.addEventListener('touchstart', (e: TouchEvent) => {
              pointerDownTime = Date.now();
              if (e.touches.length > 0) {
                const touch = e.touches[0];
                pointerDownPos = this.map.containerPointToLayerPoint(
                  L.point(touch.clientX - this.map.getContainer().getBoundingClientRect().left,
                          touch.clientY - this.map.getContainer().getBoundingClientRect().top)
                );
              }
            }, { passive: true });
          }
        });
        m.on('click', () => {
          if (this.bridge.wasMapDragged()) return;
          this.bridge.openContextMenu(point);
        });
      }
    });
  }

  // --- Punkt-Skalierung ---

  updateScaling(force = false): void {
    if (!this.map) return;
    const zoom = Math.round(this.map.getZoom());

    if (!force && zoom === this.lastScalingZoom) return;
    this.lastScalingZoom = zoom;

    const scale = Math.pow(2, zoom - 13);
    const size = Math.max(this.minSize, Math.min(this.maxSize, scale * 6));

    const points = this.bridge.getPoints();
    const pointsFilter = this.bridge.getPointsFilter();

    points?.forEach((point: MapMarker, index: number) => {
      const leafletMarker = this.leafletMarkers.get(point.id);
      leafletMarker?.setIcon(this.createSvgIcon(point.id, point.type!, size, pointsFilter && pointsFilter![index], this.selectionService.isSelected(point.id)));
    });

    this.updatePointLabelLayer();
  }

  // --- SVG-Icons ---

  private svgUrl(type: string) {
    return `assets/img/points/${type}.svg`;
  }

  private clamp(size: number, min: number = this.minFontSize, max: number = this.maxFontSize) {
    return Math.min(Math.max(size, min), max);
  }

  createSvgIcon(id: string, type: string, size: number, tagged?: boolean, selected?: boolean): L.DivIcon {
    const isAssigned = this.assignedPointIds.has(id);
    const isProjectPoint = !this.oms.offlineMode() && this.projectPointIds.has(id);
    const isFiltered = tagged ?? false;
    const isSelected = selected ?? false;

    const typeId = parseInt(type?.substring(0, type?.indexOf('_')) || '0');
    const isInvisibleType = typeId === 16 || typeId === 70;
    // Sonderstandort (150) größer darstellen
    if (typeId === 150) size = size * 1.5;

    const showAssignedHalo = isAssigned;
    const showSelectionHalo = isSelected;
    const showFilterHalo = isFiltered;

    const assignedCircleSize = size * 1.4;
    const assignedCircleOffset = (assignedCircleSize - size) / 2;
    const selectionRingWidth = 4;
    const selectionRingSize = assignedCircleSize + selectionRingWidth * 2;
    const selectionRingOffset = (selectionRingSize - size) / 2;
    const filterRingWidth = 4;
    const filterRingSize = selectionRingSize + filterRingWidth * 2;
    const filterRingOffset = (filterRingSize - size) / 2;

    const assignedHaloColor = 'rgba(0, 200, 0, 0.75)';
    const selectionRingColor = 'rgba(0, 60, 180, 0.9)';
    const filterRingColor = 'rgba(255, 140, 0, 0.9)';

    const html = `
			<div style="position: relative; width: ${size}px; height: ${size}px; overflow: visible;">
				${showFilterHalo ? `<div style="
					position: absolute;
					z-index: 0;
					width: ${filterRingSize}px;
					height: ${filterRingSize}px;
					top: -${filterRingOffset}px;
					left: -${filterRingOffset}px;
					border-radius: 50%;
					background: transparent;
					border: ${filterRingWidth}px solid ${filterRingColor};
					box-sizing: border-box;
					pointer-events: none;
				"></div>` : ''}
				${showSelectionHalo ? `<div style="
					position: absolute;
					z-index: 1;
					width: ${selectionRingSize}px;
					height: ${selectionRingSize}px;
					top: -${selectionRingOffset}px;
					left: -${selectionRingOffset}px;
					border-radius: 50%;
					background: transparent;
					border: ${selectionRingWidth}px solid ${selectionRingColor};
					box-sizing: border-box;
					pointer-events: none;
				"></div>` : ''}
				${showAssignedHalo ? `<div style="
					position: absolute;
					z-index: 2;
					width: ${assignedCircleSize}px;
					height: ${assignedCircleSize}px;
					top: -${assignedCircleOffset}px;
					left: -${assignedCircleOffset}px;
					border-radius: 50%;
					background: transparent;
					border: 3px solid ${assignedHaloColor};
					box-sizing: border-box;
					pointer-events: none;
				"></div>` : ''}
				${isInvisibleType ? '' : `<img src="${this.svgUrl(type)}" style="
					position: relative;
					z-index: 3;
					height: ${size}px;
					display: block;
					pointer-events: none;
					filter: drop-shadow(0 0 0.5px white) drop-shadow(0 0 0.5px white);
				">`}
			</div>
    `;
    return L.divIcon({
      html,
      className: '',
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2]
    });
  }

  // --- Punkt-Label Canvas ---

  updatePointLabelLayer(): void {
    const points = this.bridge.getPoints();
    if (!this.map || !points) return;

    const zoom = Math.round(this.map.getZoom());

    if (this.pointLabelLayer) {
      this.map.removeLayer(this.pointLabelLayer);
      this.pointLabelLayer = undefined;
    }

    if (zoom < this.POINT_LABEL_MIN_ZOOM) return;

    if (this.pointsLayer && !this.map.hasLayer(this.pointsLayer)) return;

    const labelData: {
      latlng: L.LatLng;
      topText?: string;
      topTextSmall?: boolean;
      sideTexts?: {text: string, color: string}[];
    }[] = [];

    const scale = Math.pow(2, zoom - 13);
    const maxFont = zoom >= 17 ? this.maxFontSize : 12;
    const fontSize = Math.max(this.minFontSize, Math.min(maxFont, scale * 2.5));

    if (fontSize <= this.minFontSize) return;

    for (const point of points) {
      if (!point.latLng) continue;

      const entry: typeof labelData[0] = { latlng: point.latLng };

      if (point.label) {
        entry.topText = point.label;
      } else if (zoom >= 16 && point.detailLabels?.bemerkung) {
        entry.topText = point.detailLabels.bemerkung;
        entry.topTextSmall = true;
      }

      if (zoom >= 16) {
        const sideTexts: {text: string, color: string}[] = [];

        if (point.detailLabels?.bodenform) {
          sideTexts.push({ text: point.detailLabels.bodenform, color: '#cc0000' });
        }
        if (point.detailLabels?.humusform) {
          sideTexts.push({ text: point.detailLabels.humusform, color: '#228B22' });
        }
        if (point.detailLabels?.stfg) {
          sideTexts.push({ text: point.detailLabels.stfg, color: '#cc0000' });
        }

        if (sideTexts.length > 0) entry.sideTexts = sideTexts;
      }

      if (entry.topText || entry.sideTexts) {
        labelData.push(entry);
      }
    }

    if (labelData.length === 0) return;

    const minFontSize = this.minFontSize;
    const pointLabelRectsRef = this.pointLabelRects;

    const PointLabelLayer = L.Layer.extend({
      initialize: function(labels: typeof labelData, options: any) {
        this._labels = labels;
        this._fontSize = options.fontSize;
        this._pointLabelRects = options.pointLabelRects;
        L.Util.setOptions(this, options);
      },

      onAdd: function(mapInstance: L.Map) {
        this._map = mapInstance;
        this._canvas = L.DomUtil.create('canvas', 'point-label-canvas') as HTMLCanvasElement;
        this._canvas.style.position = 'absolute';
        this._canvas.style.pointerEvents = 'none';
        this._canvas.style.zIndex = '10';

        const pane = mapInstance.getPane('layer-Punkte');
        if (pane) pane.appendChild(this._canvas);

        this._ctx = this._canvas.getContext('2d');

        mapInstance.on('move', this._update, this);
        mapInstance.on('resize', this._resize, this);

        this._resize();
        this._update();
      },

      onRemove: function(mapInstance: L.Map) {
        mapInstance.off('move', this._update, this);
        mapInstance.off('resize', this._resize, this);

        if (this._canvas?.parentNode) {
          this._canvas.parentNode.removeChild(this._canvas);
        }
      },

      _resize: function() {
        const size = this._map.getSize();
        this._canvas.width = size.x;
        this._canvas.height = size.y;
      },

      _update: function() {
        if (!this._map || !this._ctx) return;

        const mapRef = this._map;
        const ctx = this._ctx;
        const bounds = mapRef.getBounds();
        const size = mapRef.getSize();
        const topLeft = mapRef.containerPointToLayerPoint([0, 0]);

        L.DomUtil.setPosition(this._canvas, topLeft);
        ctx.clearRect(0, 0, size.x, size.y);

        const fs = this._fontSize;
        const fsSmall = Math.max(minFontSize, fs - 2);

        this._pointLabelRects.length = 0;

        for (const label of this._labels) {
          if (!bounds.contains(label.latlng)) continue;

          const point = mapRef.latLngToContainerPoint(label.latlng);

          if (label.topText) {
            const topFs = label.topTextSmall ? Math.max(minFontSize, fs * 0.7) : fs;
            const fontWeight = label.topTextSmall ? '400' : 'bold';
            ctx.font = `${fontWeight} ${topFs}px sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'bottom';
            const textWidth = ctx.measureText(label.topText).width;
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.7)';
            ctx.lineWidth = label.topTextSmall ? 1 : 1.5;
            ctx.strokeText(label.topText, point.x, point.y - 8);
            ctx.fillStyle = 'black';
            ctx.fillText(label.topText, point.x, point.y - 8);

            this._pointLabelRects.push({
              x: point.x - textWidth / 2,
              y: point.y - 8 - topFs,
              w: textWidth,
              h: topFs
            });
          }

          if (label.sideTexts) {
            ctx.font = `500 ${fsSmall}px sans-serif`;
            ctx.textAlign = 'left';
            ctx.textBaseline = 'top';
            let yOffset = point.y - 8;

            for (const sideText of label.sideTexts) {
              const sideTextWidth = ctx.measureText(sideText.text).width;
              ctx.strokeStyle = 'rgba(255, 255, 255, 0.7)';
              ctx.lineWidth = 1.5;
              ctx.strokeText(sideText.text, point.x + 10, yOffset);
              ctx.fillStyle = sideText.color;
              ctx.fillText(sideText.text, point.x + 10, yOffset);

              this._pointLabelRects.push({
                x: point.x + 10,
                y: yOffset,
                w: sideTextWidth,
                h: fsSmall
              });

              yOffset += fsSmall + 2;
            }
          }
        }
      }
    });

    this.pointLabelLayer = new (PointLabelLayer as any)(labelData, { fontSize, pointLabelRects: pointLabelRectsRef }) as L.Layer;
    this.map.addLayer(this.pointLabelLayer);
  }

  // --- Punkte-Änderung (Cluster-Erstellung mit Batch-Rendering) ---

  private _batchAbort?: AbortController;
  private readonly BATCH_SIZE = 500;

  onPointsChanged(): boolean {
    const points = this.bridge.getPoints();
    if (!points) return false;
    if (!this.map) return false;

    // Laufende Batch-Verarbeitung abbrechen
    this._batchAbort?.abort();
    this._batchAbort = new AbortController();
    const signal = this._batchAbort.signal;

    if (this.pointsLayer) {
      this.map.removeLayer(this.pointsLayer);
      this.pointsLayer.clearLayers();
      this.pointsLayer = undefined;
    }
    this.leafletMarkers.clear();

    // Pane für Punkte-Layer erstellen (falls noch nicht vorhanden)
    if (!this.map.getPane('layer-Punkte')) {
      const pane = this.map.createPane('layer-Punkte');
      pane.style.zIndex = '600';
    }

    // Zoom-basierte Größe berechnen
    const zoom = Math.round(this.map?.getZoom() ?? 13);
    const scale = Math.pow(2, zoom - 13);
    const markerSize = Math.max(this.minSize, Math.min(this.maxSize, scale * 6));
    this.lastScalingZoom = zoom;

    const canInteract = this.bridge.canReadPoints();
    const pointsFilter = this.bridge.getPointsFilter();

    // Index-Lookup für schnelle Punkt-Suche in Cluster-Icons (O(1) statt O(n))
    const pointIndexById = new Map<string, number>();
    points.forEach((p: MapMarker, i: number) => pointIndexById.set(p.id, i));

    this.pointsLayer = L.markerClusterGroup({
      disableClusteringAtZoom: 15,
      maxClusterRadius: 40,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      chunkedLoading: true,
      chunkInterval: 100,
      chunkDelay: 50,
      clusterPane: 'layer-Punkte',
      iconCreateFunction: (cluster: L.MarkerCluster) => {
        if (!cluster || typeof cluster.getChildCount !== 'function') {
          return L.divIcon({
            html: `<div><span>?</span></div>`,
            className: 'marker-cluster marker-cluster-small',
            iconSize: L.point(30, 30)
          });
        }
        const count = cluster.getChildCount();
        let size = 'small';
        let diameter = 30;
        if (count >= 100) {
          size = 'large';
          diameter = 50;
        } else if (count >= 10) {
          size = 'medium';
          diameter = 40;
        }

        let markers: any[] = [];
        try {
          markers = cluster.getAllChildMarkers?.() ?? [];
        } catch (e) {
          // Cluster noch nicht vollständig initialisiert
        }
        const hasAssigned = markers.some((marker: any) => {
          const bpId = marker.options?.bpId;
          return bpId && this.assignedPointIds.has(bpId);
        });
        const hasSelection = markers.some((marker: any) => {
          const bpId = marker.options?.bpId;
          return bpId && this.selectionService.isSelected(bpId);
        });
        const hasFiltered = pointsFilter && markers.some((marker: any) => {
          const bpId = marker.options?.bpId;
          if (!bpId) return false;
          const bpIndex = pointIndexById.get(bpId);
          return bpIndex !== undefined && pointsFilter![bpIndex];
        });

        const assignedClass = hasAssigned ? ' marker-cluster-has-assigned' : '';
        const selectionClass = hasSelection ? ' marker-cluster-has-selection' : '';
        const filterClass = hasFiltered ? ' marker-cluster-has-filter' : '';
        return L.divIcon({
          html: `<div><span>${count}</span></div>`,
          className: `marker-cluster marker-cluster-${size}${assignedClass}${selectionClass}${filterClass}`,
          iconSize: L.point(diameter, diameter)
        });
      }
    });

    this.bridge.addLayerToMap('Punkte', this.pointsLayer, true);

    // Marker in Batches erstellen und zur ClusterGroup hinzufügen
    const processBatch = (offset: number) => {
      if (signal.aborted) return;

      const end = Math.min(offset + this.BATCH_SIZE, points.length);
      const batchMarkers: L.Marker[] = [];

      for (let i = offset; i < end; i++) {
        const point = points[i];
        if (!point.latLng) continue;

        const m = L.marker(point.latLng, {
          icon: this.createSvgIcon(point.id, point.type!, markerSize, pointsFilter && pointsFilter[i], this.selectionService.isSelected(point.id)),
          interactive: canInteract,
          bpId: point.id,
          pane: 'layer-Punkte'
        } as any);
        this.leafletMarkers.set(point.id, m);

        if (canInteract) {
          this.attachMarkerEvents(m, point);
        }

        batchMarkers.push(m);
      }

      if (!signal.aborted && this.pointsLayer) {
        (this.pointsLayer as any).addLayers(batchMarkers);
      }

      if (end < points.length) {
        setTimeout(() => processBatch(end), 0);
      } else if (!signal.aborted) {
        // Alle Batches fertig — Canvas-Labels erstellen
        this.updatePointLabelLayer();
      }
    };

    processBatch(0);

    // Punkte-Sichtbarkeit aus Projekteinstellungen anwenden
    this.bridge.getProjectValue('pointsLayerVisible').then((visible: unknown) => {
      if ((visible as boolean | null) === false) {
        this.bridge.setPointsLayerVisibility(false);
      }
    });

    return true;
  }

  /** Event-Handler für Marker (Click, Touch, Drag) */
  private attachMarkerEvents(m: L.Marker, point: MapMarker): void {
    let pointerDownTime = 0;
    let pointerDownPos: L.Point | null = null;
    let customDragActive = false;

    m.on('mousedown', (e: L.LeafletMouseEvent) => {
      pointerDownTime = Date.now();
      pointerDownPos = e.containerPoint;

      // Custom Drag: MarkerClusterGroup fängt native drag-Events ab,
      // daher eigene Implementierung mit mousedown/mousemove/mouseup
      if (this.draggablePoints && this.bridge.onPointDragged) {
        const startLatLng = L.latLng(m.getLatLng().lat, m.getLatLng().lng);
        const startContainerPoint = e.containerPoint.clone();
        const DRAG_THRESHOLD = 5;
        customDragActive = false;

        const onMouseMove = (moveEvt: MouseEvent) => {
          const rect = this.map.getContainer().getBoundingClientRect();
          const cp = L.point(moveEvt.clientX - rect.left, moveEvt.clientY - rect.top);
          const dist = cp.distanceTo(startContainerPoint);
          if (dist < DRAG_THRESHOLD && !customDragActive) return;

          if (!customDragActive) {
            customDragActive = true;
            this.map.dragging.disable();
          }
          const newLatLng = this.map.containerPointToLatLng(cp);
          m.setLatLng(newLatLng);
        };

        const onMouseUp = () => {
          document.removeEventListener('mousemove', onMouseMove);
          document.removeEventListener('mouseup', onMouseUp);

          if (customDragActive) {
            this.map.dragging.enable();
            const newLatLng = m.getLatLng();
            const revert = () => m.setLatLng(startLatLng);
            this.bridge.onPointDragged!(point.id, newLatLng, revert);
          }
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
      }
    });

    m.once('add', () => {
      const el = m.getElement();
      if (el) {
        el.addEventListener('touchstart', (e: TouchEvent) => {
          pointerDownTime = Date.now();
          if (e.touches.length > 0) {
            const touch = e.touches[0];
            pointerDownPos = this.map.containerPointToLayerPoint(
              L.point(touch.clientX - this.map.getContainer().getBoundingClientRect().left,
                      touch.clientY - this.map.getContainer().getBoundingClientRect().top)
            );
          }
        }, { passive: true });
      }
    });

    m.on('click', () => {
      if (customDragActive) { customDragActive = false; return; }
      if (this.bridge.wasMapDragged()) return;
      this.bridge.openContextMenu(point);
    });
  }

  // --- Selection-Highlighting ---

  updateSelectionHighlighting(): void {
    const points = this.bridge.getPoints();
    const pointsFilter = this.bridge.getPointsFilter();

    points?.forEach((point: MapMarker, index: number) => {
      const leafletMarker = this.leafletMarkers.get(point.id);
      if (leafletMarker) {
        const isSelected = this.selectionService.isSelected(point.id);
        const scale = Math.pow(2, (this.map?.getZoom() || 13) - 13);
        const size = Math.max(this.minSize, Math.min(this.maxSize, scale * 8));
        leafletMarker.setIcon(this.createSvgIcon(point.id, point.type!, size, pointsFilter?.[index], isSelected));
      }
    });
  }

  updateClusterIcons(): void {
    if (!this.pointsLayer) return;
    (this.pointsLayer as any).refreshClusters?.();
  }

  // --- Grid-Punkte ---

  createGridPoints(ld: LayerDetails, warn = false): L.LayerGroup {
    if (!this.map || !ld.resolution || !ld.bbox) return L.layerGroup();
    const bounds = this.map.getBounds() as any;
    const spacing = Math.sqrt(ld.resolution! * 10000);

    const northEast = this.map.project(bounds._northEast, this.map.getZoom());
    const southWest = this.map.project(bounds._southWest, this.map.getZoom());

    const spacingPx = this.metersToPixels(spacing);

    const features: L.Layer[] = [];
    if ((southWest.y - northEast.y) / spacingPx > 200 || (northEast.x - southWest.x)/spacingPx > 200) {
      if (warn) this.msg.warning("Das Punktgitter enthält zu viele Punkte. Bitte näher heranzoomen.");
      return L.layerGroup();
    }

    const style = ld.gridStyle ?? { color: '#3388ff', fillColor: '#3388ff', fillOpacity: 0.5, radius: 3 };

    const paneName = 'layer-Punktraster';
    if (!this.map.getPane(paneName)) {
      const pane = this.map.createPane(paneName);
      pane.style.zIndex = '600';
    }

    for (let y = northEast.y; y <= southWest.y; y += spacingPx) {
      for (let x = southWest.x; x <= northEast.x; x += spacingPx) {
        const latlng = this.map.unproject(L.point(x, y), this.map.getZoom());
        features.push(L.circleMarker(latlng, {
          radius: style.radius,
          color: style.color,
          opacity: style.fillOpacity,
          fillColor: style.fillColor,
          fillOpacity: style.fillOpacity,
          weight: 1,
          interactive: false,
          pane: paneName
        }));
      }
    }

    return L.layerGroup(features);
  }

  updatePointGrid(warn = false): void {
    if (!this.gridLayer) return;
    if (this.gridLayer.l) this.gridLayer.l.remove();
    this.gridLayer.l = this.createGridPoints(this.gridLayer.ld, warn);
    this.bridge.addLayerToMap('Punktgitter', this.gridLayer.l, true);
  }

  private metersToPixels(meters: number): number {
    const center = this.map.getCenter();
    const pointA = this.map.latLngToContainerPoint(center);
    const pointB = this.map.latLngToContainerPoint((L.GeometryUtil as any)?.destination(center, 90, meters));
    return Math.abs(pointB.x - pointA.x);
  }
}
